import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { companyService } from '../../services/companyService';
import { stripeService } from '../../services/stripeService';
import { sanitizeError } from '../../utils/sanitizeError';
import { useAuth } from '../../contexts/AuthContext';
import {
  MONTHLY_PRICE_PER_SITE,
  ANNUAL_PRICE_PER_SITE,
  MIN_SITES,
  PRICING_VERSION_GRADUATED,
  calculatePriceForVersion,
} from '../../constants/planConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';

const ACTIVE_STATUSES = ['active', 'trialing', 'past_due'];

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const { t } = useLanguage();

  const [company, setCompany] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState('');

  // Site count update state
  const [newSiteCount, setNewSiteCount] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState('');
  const [updateError, setUpdateError] = useState('');

  // Detect post-checkout return
  const isCheckoutReturn = new URLSearchParams(location.search)?.get('checkout') === 'success';

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Calls the sync-subscription edge function directly for the given company.
   * Returns the full response payload so trial_end_date can be used directly
   * from Stripe without relying on the DB value.
   */
  const syncFromStripe = async (companyId) => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) return null;

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/sync-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ companyId }),
        }
      );

      if (!response?.ok) return null;
      const result = await response?.json()?.catch(() => ({}));
      return result?.synced === true ? result : null;
    } catch (_) {
      return null;
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const { data: companyData, error: companyError } = await companyService?.getUserCompany();
      if (companyError) throw companyError;

      // Always sync from Stripe on load to get the latest subscription data,
      // including trial_end_date directly from the Stripe API response.
      if (companyData?.id) {
        setSyncing(true);
        const syncResult = await syncFromStripe(companyData?.id);
        setSyncing(false);

        // Re-fetch company from DB after sync (to get all updated fields)
        const { data: refreshed } = await companyService?.getUserCompany();
        const latestCompany = refreshed ?? companyData;

        // If Stripe returned a trial_end_date directly, override the DB value
        // so the UI always shows the correct date even if the DB write lagged.
        if (syncResult?.trial_end_date && latestCompany) {
          latestCompany.trial_end_date = syncResult?.trial_end_date;
        }

        setCompany(latestCompany);
        if (latestCompany?.id) {
          const { data: usageData } = await companyService?.getActiveSiteUsage(latestCompany?.id);
          setUsage(usageData);
        }
        return;
      }

      setCompany(companyData);
      if (companyData?.id) {
        const { data: usageData } = await companyService?.getActiveSiteUsage(companyData?.id);
        setUsage(usageData);
      }
    } catch (err) {
      console.error('[SubscriptionManagement] load data failed:', err);
      setError('Failed to load subscription data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    if (!company?.id) return;
    setPortalLoading(true);
    setError('');
    try {
      const { data, error: portalError } = await stripeService?.createPortalSession(company?.id);
      if (portalError) throw portalError;
      if (data?.url) window.location.href = data?.url;
    } catch (err) {
      console.error('[SubscriptionManagement] open portal failed:', err);
      // sanitizeError surfaces actionable Stripe detail when present
      // (e.g. "No such customer" → reconnect message), otherwise falls
      // back to the generic copy. Per card hsGzqWrI.
      setError(sanitizeError(err?.message, 'Failed to open billing portal. Please try again.'));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSyncSubscription = async () => {
    if (!company?.id) return;
    setSyncLoading(true);
    setSyncSuccess('');
    setError('');
    try {
      const { data, error: syncError } = await stripeService?.syncSubscription(company?.id);
      if (syncError) throw syncError;
      setSyncSuccess(`Subscription synced successfully. Status: ${data?.status ?? 'updated'}`);
      await loadData();
    } catch (err) {
      console.error('[SubscriptionManagement] sync subscription failed:', err);
      setError(sanitizeError(err?.message, 'Failed to sync subscription. Please try again.'));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUpdateSites = async () => {
    const count = parseInt(newSiteCount, 10);
    if (!count || count < MIN_SITES) return;
    setUpdateLoading(true);
    setUpdateError('');
    setUpdateSuccess('');
    try {
      const { data, error: updateErr } = await stripeService?.updateSubscription(company?.id, count);
      if (updateErr) throw updateErr;
      setUpdateSuccess(`Site count updated to ${count}. Your billing has been adjusted.`);
      setShowConfirm(false);
      setNewSiteCount('');
      // Refresh company data to reflect new max_active_sites
      await loadData();
    } catch (err) {
      console.error('[SubscriptionManagement] update sites failed:', err);
      // Was: `setUpdateError(err?.message || …)` — leaked raw "Stripe error:
      // No such customer: cus_xxx" through to the user. sanitizeError
      // translates known Stripe error shapes and falls back to the generic
      // copy for everything else. Per card hsGzqWrI.
      setUpdateError(sanitizeError(err?.message, 'Failed to update site count. Please try again or contact support.'));
    } finally {
      setUpdateLoading(false);
    }
  };

  const statusBadge = stripeService?.getStatusBadge(company?.subscription_status);

  const billingInterval = company?.billing_interval ?? null;
  const isMonthly = billingInterval === 'monthly';
  const isAnnual = billingInterval === 'annual';

  const hasActiveSubscription =
    ACTIVE_STATUSES?.includes(company?.subscription_status) &&
    (billingInterval !== null || company?.subscription_status === 'trialing');

  const isPendingWebhook =
    !hasActiveSubscription &&
    company?.plan_tier &&
    (!company?.subscription_status || company?.subscription_status === 'inactive') &&
    billingInterval === null;

  const maxSites = company?.max_active_sites ?? 0;
  const activeSites = usage?.active_count ?? usage?.active_sites ?? 0;
  const usagePct = maxSites ? Math.min(100, Math.round((activeSites / maxSites) * 100)) : 0;

  // Pricing version branches the display between legacy flat rate and the
  // new graduated tiers. NULL is treated as legacy for display only — the
  // webhook will stamp the real value on the next Stripe event.
  const pricingVersion = company?.pricing_version ?? null;
  const isGraduated = pricingVersion === PRICING_VERSION_GRADUATED;
  const billingIntervalLabel = isMonthly ? 'monthly' : 'annual';

  // Current bill display. For graduated subs we compute a tier breakdown.
  const currentPricing = maxSites >= MIN_SITES
    ? calculatePriceForVersion(maxSites, billingInterval ?? 'monthly', pricingVersion)
    : null;
  const flatPricePerSite = isAnnual ? ANNUAL_PRICE_PER_SITE : MONTHLY_PRICE_PER_SITE;
  const pricePerSite = isGraduated
    ? (currentPricing?.effectivePerSite ?? flatPricePerSite)
    : flatPricePerSite;
  const currentTotal = currentPricing?.monthlyTotal ?? (maxSites * flatPricePerSite);

  // Confirm step pricing preview
  const confirmCount = parseInt(newSiteCount, 10) || 0;
  const confirmPricing = confirmCount >= MIN_SITES
    ? calculatePriceForVersion(confirmCount, billingInterval ?? 'monthly', pricingVersion)
    : null;

  // Trial info
  const isTrialing = company?.subscription_status === 'trialing';
  const trialEndDate = company?.trial_end_date || company?.current_period_end;
  const daysUntilCharge = trialEndDate
    ? Math.max(0, Math.ceil((new Date(trialEndDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  // Show trial banner when trialing OR when subscription is inactive/pending but trial_end_date exists
  const showTrialBanner = (isTrialing || (!hasActiveSubscription && company?.trial_end_date)) && trialEndDate;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading || syncing) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center h-64 mt-16 gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
          {syncing && (
            <p className="text-sm text-muted-foreground">Fetching your subscription from Stripe…</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 pt-20 pb-12">

        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('subscriptionBilling')}</h1>
            <p className="text-muted-foreground mt-1">{t('subscriptionBillingSubtitle')}</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/invoice-history')} iconName="FileText" iconPosition="left">
            {t('invoiceHistory')}
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* No-subscription sync prompt */}
        {!hasActiveSubscription && !loading && !syncing && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Icon name="RefreshCw" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-800">Already subscribed? No need to create a new account.</p>
                <p className="text-sm text-blue-700 mt-1">
                  If you recently subscribed on Stripe, click <strong>Sync from Stripe</strong> below to pull your subscription into this account. This links your existing Stripe subscription to your current account automatically.
                </p>
                <Button
                  variant="default"
                  className="mt-3"
                  onClick={handleSyncSubscription}
                  disabled={syncLoading}
                  iconName={syncLoading ? 'Loader' : 'RefreshCw'}
                  iconPosition="left"
                >
                  {syncLoading ? 'Syncing from Stripe…' : 'Sync from Stripe'}
                </Button>
              </div>
            </div>
            {syncSuccess && (
              <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">{syncSuccess}</p>
              </div>
            )}
          </div>
        )}

        {/* Trial Banner */}
        {showTrialBanner && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
            <Icon name="Clock" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800">
                You're on your 30-day free trial
                {daysUntilCharge !== null && daysUntilCharge > 0 && ` · ${daysUntilCharge} day${daysUntilCharge !== 1 ? 's' : ''} remaining`}
              </p>
              <p className="text-sm text-blue-700 mt-0.5">
                Your trial ends on <strong>{formatDate(trialEndDate)}</strong>. You won't be charged if you cancel before that date.
              </p>
              <ul className="mt-2 space-y-0.5 text-xs text-blue-600">
                <li>• Full access to all features during your trial</li>
                <li>• Cancel anytime before billing — you won't be charged</li>
              </ul>
            </div>
          </div>
        )}

        {/* Subscription Status Card */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('currentPlan')}</h2>
              <p className="text-muted-foreground text-sm mt-0.5">{t('currentPlanSubtitle')}</p>
            </div>
            {company?.subscription_status && (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusBadge?.className}`}>
                {statusBadge?.label}
              </span>
            )}
          </div>

          {!hasActiveSubscription ? (
            <div className="flex items-start gap-3 p-4 bg-muted/40 border border-border rounded-lg">
              <Icon name="Info" size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                {isPendingWebhook ? (
                  <>
                    <p className="text-sm font-medium text-foreground">Payment received — activating your subscription</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Your payment was successful. Subscription activation usually takes under a minute.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={loadData}
                      iconName="RefreshCw"
                      iconPosition="left"
                    >
                      Refresh
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-foreground">{t('noActiveSubscription')}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {t('noActiveSubscriptionDesc')}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Pricing Model</p>
                <p className="text-xl font-bold text-foreground">{isGraduated ? 'Per Site · Tiered' : 'Per Site'}</p>
                <p className="text-sm text-muted-foreground">
                  {isGraduated
                    ? `~£${Math.round(pricePerSite)}/site/${billingIntervalLabel === 'monthly' ? 'month' : 'month eq.'} blended`
                    : `£${pricePerSite}/site/${billingIntervalLabel === 'monthly' ? 'month' : 'month eq.'}`}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('billingCycle')}</p>
                <p className="text-base font-semibold text-foreground capitalize">{billingInterval ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Active Sites Allowed</p>
                <p className="text-base font-semibold text-foreground">{maxSites > 0 ? `${maxSites} sites` : '—'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Site Usage — only shown when subscription is active */}
        {hasActiveSubscription && (
          <div className="bg-card border border-border rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t('siteUsageBillingBreakdown')}</h2>

            {/* Usage bar */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-muted-foreground">{t('activeSitesVsAllowance')}</span>
                <span className="text-sm font-semibold text-foreground">
                  {activeSites} / {maxSites > 0 ? maxSites : '∞'} sites
                </span>
              </div>
              {maxSites > 0 && (
                <div className="w-full bg-muted rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      usagePct >= 90 ? 'bg-destructive' : usagePct >= 70 ? 'bg-warning' : 'bg-accent'
                    }`}
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
              )}
              {maxSites > 0 && usagePct >= 80 && (
                <p className="text-xs text-warning mt-1.5">
                  {t('usingPercentOfAllowance', { pct: usagePct })}
                </p>
              )}
            </div>

            {/* Billing breakdown */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
                  <div>
                    <p className="text-sm font-medium text-foreground">Site Allowance</p>
                    <p className="text-xs text-muted-foreground">
                      {isGraduated && currentPricing?.breakdown?.length
                        ? currentPricing.breakdown.map((t) => `${t.sites} × £${t.pricePerSite}`).join(' + ')
                        : `${maxSites} site${maxSites !== 1 ? 's' : ''} × £${pricePerSite}/site/${isMonthly ? 'month' : 'month eq.'}`}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    £{currentTotal.toLocaleString('en-GB')}/{isMonthly ? 'month' : 'month eq.'}
                  </p>
                </div>
                <div className="flex items-center justify-between px-4 py-3 bg-accent/5">
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      Total {isMonthly ? 'Monthly' : 'Annual'} Cost
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isMonthly
                        ? 'Billed once per month'
                        : `£${(currentTotal * 12).toLocaleString('en-GB')}/year · billed annually`}
                    </p>
                  </div>
                  <p className="text-base font-bold text-foreground">
                    {isMonthly
                      ? `£${currentTotal.toLocaleString('en-GB')}/month`
                      : `£${(currentTotal * 12).toLocaleString('en-GB')}/year`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Site Count — only shown when subscription is active */}
        {hasActiveSubscription && (
          <div className="bg-card border border-border rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-1">Manage Site Count</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Add or remove sites from your subscription. Your billing will be adjusted automatically.
            </p>

            <div className="flex items-center gap-2 mb-2">
              <p className="text-sm text-muted-foreground">
                Current allowance: <span className="font-semibold text-foreground">{maxSites} site{maxSites !== 1 ? 's' : ''}</span>
              </p>
            </div>

            {!showConfirm ? (
              <div className="flex items-end gap-3 mt-3">
                <div className="flex-1 max-w-xs">
                  <label className="block text-sm font-medium text-foreground mb-1" htmlFor="site-count-input">
                    New site count
                  </label>
                  <input
                    id="site-count-input"
                    type="number"
                    min={MIN_SITES}
                    value={newSiteCount}
                    onChange={(e) => {
                      setNewSiteCount(e.target.value);
                      setUpdateError('');
                      setUpdateSuccess('');
                    }}
                    placeholder={`Min ${MIN_SITES}`}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                <Button
                  variant="default"
                  onClick={() => {
                    const count = parseInt(newSiteCount, 10);
                    if (!count || count < MIN_SITES) {
                      setUpdateError(`Minimum site count is ${MIN_SITES}.`);
                      return;
                    }
                    setShowConfirm(true);
                  }}
                  disabled={!newSiteCount}
                >
                  Update Sites
                </Button>
              </div>
            ) : (
              <div className="mt-3 p-4 bg-muted/40 border border-border rounded-lg">
                <p className="text-sm font-semibold text-foreground mb-1">Confirm site count change</p>
                {confirmPricing && (
                  <p className="text-sm text-muted-foreground mb-3">
                    Your billing will change to{' '}
                    <strong className="text-foreground">{confirmPricing.siteCount} sites</strong> —{' '}
                    {isGraduated && confirmPricing.breakdown?.length ? (
                      <strong className="text-foreground">
                        {confirmPricing.breakdown.map((tier) => `${tier.sites} × £${tier.pricePerSite}`).join(' + ')}
                      </strong>
                    ) : (
                      <strong className="text-foreground">
                        £{confirmPricing.pricePerSite}/site/{isMonthly ? 'month' : 'month eq.'}
                      </strong>
                    )}
                    {' '}={' '}
                    <strong className="text-foreground">
                      £{confirmPricing.monthlyTotal.toLocaleString('en-GB')}/month{isAnnual ? ' eq.' : ''}
                    </strong>
                    {isAnnual && (
                      <span> (£{confirmPricing.annualTotal.toLocaleString('en-GB')}/year billed annually)</span>
                    )}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mb-4">
                  Stripe will automatically prorate the difference on your next invoice.
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    variant="default"
                    onClick={handleUpdateSites}
                    disabled={updateLoading}
                    iconName={updateLoading ? 'Loader' : 'Check'}
                    iconPosition="left"
                  >
                    {updateLoading ? 'Updating…' : 'Confirm Update'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowConfirm(false);
                      setUpdateError('');
                    }}
                    disabled={updateLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {updateError && (
              <div className="mt-3 flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
                <p className="text-sm text-destructive">{updateError}</p>
              </div>
            )}

            {updateSuccess && (
              <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">{updateSuccess}</p>
              </div>
            )}
          </div>
        )}

        {/* Billing Portal */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('manageSubscription')}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t('manageSubscriptionDesc')}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleOpenPortal}
              disabled={portalLoading || !company?.stripe_customer_id}
              iconName="ExternalLink"
              iconPosition="right"
            >
              {portalLoading ? t('opening') : t('manageSubscription')}
            </Button>
          </div>
          {!company?.stripe_customer_id && (
            <p className="text-xs text-muted-foreground mt-3">
              {t('billingPortalAvailableAfterPayment')}
            </p>
          )}
        </div>

        {/* Sync Subscription */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Sync Subscription</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                If your subscription status looks incorrect, use this to force-refresh it directly from Stripe.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleSyncSubscription}
              disabled={syncLoading}
              iconName={syncLoading ? 'Loader' : 'RefreshCw'}
              iconPosition="left"
              className="flex-shrink-0"
            >
              {syncLoading ? 'Syncing…' : 'Sync from Stripe'}
            </Button>
          </div>
          {syncSuccess && (
            <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700">{syncSuccess}</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SubscriptionManagement;
